/**
 * 관리자 인덱스 공통: 행 다중 선택 + 대량 삭제.
 *
 * 마크업 계약:
 *   <div data-bulk-target-scope
 *        data-bulk-url="/console/site/works/bulk"   ← DELETE /resources/bulk
 *        data-bulk-resource="works"> (toolbar 래퍼)
 *     <button data-bulk-action="select-all|deselect|delete">...</button>
 *     <span data-bulk-count>0</span>
 *   </div>
 *   <table data-bulk-target>
 *     <thead><th><input type="checkbox" data-bulk-master></th></thead>
 *     <tbody><tr><td><input type="checkbox" data-bulk-row value="<id>"></td></tr></tbody>
 *   </table>
 *
 * RESTful: DELETE /resources/bulk  body FormData: ids[]  (fetch _method=DELETE 또는 실제 DELETE)
 * 자동 부착: initBulkSelect() 가 [data-bulk-target] 테이블마다 인스턴스를 만든다.
 */
import { Modal } from "../../utils/Modal";
import { Toast } from "../../utils/Toast";

interface BulkSelectOptions {
    /** POST 대상 — bulk-destroy 엔드포인트 */
    url: string;
    /** 토스트/디버그용 라벨 */
    resource: string;
}

interface BulkResponse {
    result?: string;
    msg?: string;
    deleted?: number;
    skipped?: number;
}

function getCsrfToken(scope: HTMLElement): string {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    if (meta?.content) return meta.content;
    const inputInScope = scope.querySelector<HTMLInputElement>('input[name="_token"]');
    if (inputInScope?.value) return inputInScope.value;
    const inputAny = document.querySelector<HTMLInputElement>('input[name="_token"]');
    return inputAny?.value ?? "";
}

export class BulkSelectManager {
    readonly #table: HTMLTableElement;
    readonly #opts: BulkSelectOptions;
    #scope: HTMLElement | null = null;
    #master: HTMLInputElement | null = null;
    #rows: HTMLInputElement[] = [];
    #countEl: HTMLElement | null = null;
    #btnSelectAll: HTMLButtonElement | null = null;
    #btnDeselect: HTMLButtonElement | null = null;
    #btnDelete: HTMLButtonElement | null = null;
    #busy = false;

    constructor(table: HTMLTableElement, opts: BulkSelectOptions) {
        this.#table = table;
        this.#opts = opts;
    }

    init(): void {
        this.#scope = this.#findScope();
        this.#master = this.#table.querySelector<HTMLInputElement>("[data-bulk-master]");
        this.#refreshRowList();

        if (this.#scope) {
            this.#countEl = this.#scope.querySelector<HTMLElement>("[data-bulk-count]");
            this.#btnSelectAll = this.#scope.querySelector<HTMLButtonElement>('[data-bulk-action="select-all"]');
            this.#btnDeselect = this.#scope.querySelector<HTMLButtonElement>('[data-bulk-action="deselect"]');
            this.#btnDelete = this.#scope.querySelector<HTMLButtonElement>('[data-bulk-action="delete"]');
        }

        this.#master?.addEventListener("change", () => {
            if (!this.#master) return;
            this.#master.checked ? this.selectAll() : this.deselectAll();
        });

        // 위임: 행 체크박스 이벤트는 tbody 에 한 번만 바인드 (행 추가/제거 대비)
        this.#table.addEventListener("change", (e) => {
            const t = e.target as HTMLElement | null;
            if (t instanceof HTMLInputElement && t.matches("[data-bulk-row]")) {
                this.#syncState();
            }
        });

        this.#btnSelectAll?.addEventListener("click", () => this.selectAll());
        this.#btnDeselect?.addEventListener("click", () => this.deselectAll());
        this.#btnDelete?.addEventListener("click", () => void this.#triggerDelete());

        this.#syncState();
    }

    selectAll(): void {
        this.#refreshRowList();
        this.#rows.forEach((cb) => {
            if (!cb.disabled) cb.checked = true;
        });
        this.#syncState();
    }

    deselectAll(): void {
        this.#refreshRowList();
        this.#rows.forEach((cb) => (cb.checked = false));
        this.#syncState();
    }

    getSelectedIds(): number[] {
        this.#refreshRowList();
        const ids: number[] = [];
        this.#rows.forEach((cb) => {
            if (!cb.checked) return;
            const id = Number(cb.value || "0") || 0;
            if (id > 0) ids.push(id);
        });
        return ids;
    }

    #findScope(): HTMLElement | null {
        // 1) 테이블이 토글 영역 내부에 있을 때 (드물지만 안전망)
        const ancestor = this.#table.closest<HTMLElement>("[data-bulk-target-scope]");
        if (ancestor) return ancestor;

        // 2) 같은 페이지 내에서 data-bulk-resource 값이 일치하는 토글바를 찾는다 (대표 케이스)
        const resource = (this.#opts.resource || this.#table.dataset.bulkResource || "").trim();
        if (resource) {
            try {
                const escaped = window.CSS && typeof window.CSS.escape === "function"
                    ? window.CSS.escape(resource)
                    : resource.replace(/"/g, '\\"');
                const byResource = document.querySelector<HTMLElement>(
                    `[data-bulk-target-scope][data-bulk-resource="${escaped}"]`,
                );
                if (byResource) return byResource;
            } catch {
                // fall through
            }
        }

        // 3) 형제 트리를 따라 올라가며 토글바를 찾는다 (resource 가 없는 경우 마지막 수단)
        let parent: HTMLElement | null = this.#table.parentElement;
        while (parent && parent !== document.body) {
            const found = parent.querySelector<HTMLElement>("[data-bulk-target-scope]");
            if (found) return found;
            parent = parent.parentElement;
        }
        return null;
    }

    #refreshRowList(): void {
        this.#rows = Array.from(this.#table.querySelectorAll<HTMLInputElement>("tbody [data-bulk-row]"));
    }

    #syncState(): void {
        this.#refreshRowList();
        const total = this.#rows.length;
        const checked = this.#rows.filter((cb) => cb.checked).length;

        if (this.#master) {
            this.#master.checked = total > 0 && checked === total;
            this.#master.indeterminate = checked > 0 && checked < total;
        }
        if (this.#countEl) this.#countEl.textContent = String(checked);

        const hasAny = checked > 0;
        if (this.#btnDeselect) this.#btnDeselect.disabled = !hasAny;
        if (this.#btnDelete) this.#btnDelete.disabled = !hasAny || this.#busy;

        const scope = this.#scope;
        if (scope) {
            scope.classList.toggle("is-active", hasAny);
        }
    }

    async #triggerDelete(): Promise<void> {
        if (this.#busy) return;
        const ids = this.getSelectedIds();
        if (ids.length === 0) return;

        const confirmed = await Modal.confirm(`${ids.length}건을 삭제할까요? 이 작업은 되돌릴 수 없습니다.`);
        if (!confirmed) return;

        if (!this.#opts.url) {
            Toast.error("대량 삭제 엔드포인트가 설정되지 않았습니다.");
            return;
        }

        this.#busy = true;
        this.#syncState();

        const fd = new FormData();
        const token = getCsrfToken(this.#scope ?? this.#table);
        if (token) fd.append("_token", token);
        fd.append("_method", "DELETE");
        ids.forEach((id) => fd.append("ids[]", String(id)));

        try {
            const res = await fetch(this.#opts.url, {
                method: "POST",
                body: fd,
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    ...(token ? { "X-CSRF-TOKEN": token } : {}),
                },
                credentials: "same-origin",
            });

            const raw = await res.text();
            let data: BulkResponse = {};
            try {
                data = raw ? (JSON.parse(raw) as BulkResponse) : {};
            } catch {
                data = {};
            }

            if (!res.ok || data.result !== "success") {
                Toast.error(data.msg || "대량 삭제에 실패했습니다.");
                this.#busy = false;
                this.#syncState();
                return;
            }

            // 성공: 토스트 띄운 뒤 새로고침 (페이지네이션·정렬 재계산을 단순화)
            const deleted = Number(data.deleted ?? ids.length);
            const skipped = Number(data.skipped ?? 0);
            const summary =
                data.msg ||
                (skipped > 0
                    ? `${deleted}건 삭제, ${skipped}건은 건너뜀`
                    : `${deleted}건 삭제했습니다.`);
            Toast.success(summary);
            window.setTimeout(() => window.location.reload(), 350);
        } catch (e) {
            Toast.error("네트워크 오류로 삭제를 완료하지 못했습니다.");
            this.#busy = false;
            this.#syncState();
        }
    }
}
